import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Globe,
  FileText,
  Image as ImageIcon,
  Award,
  Loader2,
  AlertTriangle,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { apiService, Establishment, Manager } from "@/services/api";

export default function EstablishmentDetails() {
  const { establishmentId } = useParams();
  const navigate = useNavigate();
  const [establishment, setEstablishment] = useState<Establishment | null>(null);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Fetch establishment data from API
  useEffect(() => {
    const fetchEstablishmentData = async () => {
      if (!establishmentId) {
        toast.error("Establishment ID is missing");
        navigate("/establishments");
        return;
      }

      try {
        setIsLoading(true);
        const [establishmentResponse, managersResponse] = await Promise.all([
          apiService.getEstablishmentById(establishmentId),
          apiService.getManagersByEstablishmentId(establishmentId),
        ]);
        
        if (establishmentResponse.type === 0 && establishmentResponse.data) {
          setEstablishment(establishmentResponse.data);
        } else {
          toast.error(establishmentResponse.message || "Failed to load establishment data");
          navigate("/establishments");
        }

        if (managersResponse.type === 0 && managersResponse.data) {
          setManagers(managersResponse.data);
        }
      } catch (error) {
        console.error("Error fetching establishment data:", error);
        toast.error("Failed to load establishment details");
        navigate("/establishments");
      } finally {
        setIsLoading(false);
      }
    };

    void fetchEstablishmentData();
  }, [establishmentId, navigate]);

  // Removed - not implemented yet
  // const handleEditEstablishment = () => {
  //   toast.info("Edit establishment functionality coming soon");
  // };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading establishment details...</p>
        </div>
      </div>
    );
  }

  if (!establishment) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Establishment Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The requested establishment could not be found.
          </p>
          <Button
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Establishment Details</h1>
            <p className="text-muted-foreground">
              Managing {establishment.establishmentName}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => {
              navigate(`/users/${establishment.userId}`);
            }}
          >
            <User className="w-4 h-4 mr-2" />
            View Owner
          </Button>
        </div>
      </div>

      {/* Establishment Overview Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <Avatar className="w-20 h-20">
                <AvatarImage
                  src={establishment.establishmentLogo?.fileUrl}
                  alt={establishment.establishmentName}
                />
                <AvatarFallback>
                  {establishment.establishmentName
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-2xl">{establishment.establishmentName}</CardTitle>
                <CardDescription className="text-lg">{establishment.serviceType}</CardDescription>
                <div className="flex items-center space-x-2 mt-2">
                  {establishment.specialty && (
                    <Badge variant="secondary">{establishment.specialty}</Badge>
                  )}
                  {establishment.yearOfExperience && (
                    <Badge variant="outline">
                      {establishment.yearOfExperience} years experience
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            <div className="text-right text-sm text-muted-foreground">
              <div>Created on {new Date(establishment.createdAt).toLocaleDateString()}</div>
              {establishment.registrationNumber && (
                <div className="mt-1">Reg #: {establishment.registrationNumber}</div>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="certificates">Certificates</TabsTrigger>
          <TabsTrigger value="images">Images</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Basic Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center space-x-2">
                  <Building2 className="w-5 h-5" />
                  <span>Basic Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Name:</span>
                  <span className="font-medium">{establishment.establishmentName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Service Type:</span>
                  <span className="font-medium">{establishment.serviceType}</span>
                </div>
                {establishment.specialty && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Specialty:</span>
                    <span className="font-medium">{establishment.specialty}</span>
                  </div>
                )}
                {establishment.yearOfExperience && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Experience:</span>
                    <span className="font-medium">{establishment.yearOfExperience} years</span>
                  </div>
                )}
                {establishment.registrationNumber && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Registration #:</span>
                    <span className="font-medium">{establishment.registrationNumber}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Location Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center space-x-2">
                  <MapPin className="w-5 h-5" />
                  <span>Location</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {establishment.address?.street && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Address:</span>
                    <span className="font-medium text-right">{establishment.address.street}</span>
                  </div>
                )}
                {establishment.address?.city && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">City:</span>
                    <span className="font-medium">{establishment.address.city}</span>
                  </div>
                )}
                {establishment.address?.state && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">State:</span>
                    <span className="font-medium">{establishment.address.state}</span>
                  </div>
                )}
                {establishment.address?.postalCode && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Postal Code:</span>
                    <span className="font-medium">{establishment.address.postalCode}</span>
                  </div>
                )}
                {establishment.radius && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Service Radius:</span>
                    <span className="font-medium">{establishment.radius} miles</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Description */}
          {establishment.establishmentDetails && (
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{establishment.establishmentDetails}</p>
              </CardContent>
            </Card>
          )}

          {/* Website */}
          {establishment.websiteLink && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Globe className="w-5 h-5" />
                  <span>Website</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <a
                  href={establishment.websiteLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {establishment.websiteLink}
                </a>
              </CardContent>
            </Card>
          )}

          {/* Managers */}
          {managers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <User className="w-5 h-5" />
                  <span>Managers ({managers.length})</span>
                </CardTitle>
                <CardDescription>
                  Managers assigned to this establishment
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {managers.map((manager) => {
                    const user = typeof manager.userId === "object" ? manager.userId : null;
                    const fullName = user?.fullName || "N/A";
                    const userIdStr = typeof manager.userId === "string" ? manager.userId : user?._id || "";
                    
                    return (
                      <div
                        key={manager._id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                        onClick={() => {
                          navigate(`/users/${userIdStr}`);
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage
                              src={user?.profilePicture?.fileUrl || manager.profilePicture?.fileUrl}
                              alt={fullName}
                            />
                            <AvatarFallback>
                              {fullName.split(" ").map(n => n[0]).join("").toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium">{fullName}</div>
                            <div className="text-sm text-muted-foreground">
                              {user?.email || "No email"}
                            </div>
                            {manager.experience && (
                              <div className="text-xs text-muted-foreground">
                                Experience: {manager.experience}
                              </div>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/users/${userIdStr}`);
                          }}
                        >
                          View Profile
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Establishment Details</CardTitle>
              <CardDescription>Complete information about this establishment</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-3">Basic Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Establishment Name:</span>
                      <span className="font-medium">{establishment.establishmentName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Service Type:</span>
                      <span className="font-medium">{establishment.serviceType}</span>
                    </div>
                    {establishment.specialty && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Specialty:</span>
                        <span className="font-medium">{establishment.specialty}</span>
                      </div>
                    )}
                    {establishment.yearOfExperience && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Experience:</span>
                        <span className="font-medium">{establishment.yearOfExperience} years</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-3">Location Details</h4>
                  <div className="space-y-2">
                    {establishment.address?.street && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Address:</span>
                        <span className="font-medium text-right">{establishment.address.street}</span>
                      </div>
                    )}
                    {establishment.address?.city && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">City:</span>
                        <span className="font-medium">{establishment.address.city}</span>
                      </div>
                    )}
                    {establishment.address?.state && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">State:</span>
                        <span className="font-medium">{establishment.address.state}</span>
                      </div>
                    )}
                    {establishment.address?.postalCode && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Postal Code:</span>
                        <span className="font-medium">{establishment.address.postalCode}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {establishment.establishmentDetails && (
                <div>
                  <h4 className="font-medium mb-3">Description</h4>
                  <div className="p-3 bg-muted rounded-lg">
                    <p>{establishment.establishmentDetails}</p>
                  </div>
                </div>
              )}

              {establishment.coordinates && (
                <div>
                  <h4 className="font-medium mb-3">Coordinates</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-muted rounded-lg">
                      <div className="text-sm text-muted-foreground">Latitude</div>
                      <div className="font-medium">{establishment.coordinates.latitude}</div>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <div className="text-sm text-muted-foreground">Longitude</div>
                      <div className="font-medium">{establishment.coordinates.longitude}</div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Certificates Tab */}
        <TabsContent value="certificates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Award className="w-5 h-5" />
                <span>Certificates & Documents</span>
              </CardTitle>
              <CardDescription>
                Uploaded certificates and verification documents
              </CardDescription>
            </CardHeader>
            <CardContent>
              {establishment.certificates && establishment.certificates.length > 0 ? (
                <div className="space-y-4">
                  {establishment.certificates.map((cert, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <div className="font-medium">Certificate {index + 1}</div>
                          <div className="text-sm text-muted-foreground">
                            {cert.fileType} • {(cert.size / 1024).toFixed(2)} KB
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(cert.fileUrl, "_blank")}
                      >
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No certificates uploaded yet
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Images Tab */}
        <TabsContent value="images" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <ImageIcon className="w-5 h-5" />
                <span>Establishment Images</span>
              </CardTitle>
              <CardDescription>Photos and visual content</CardDescription>
            </CardHeader>
            <CardContent>
              {establishment.establishmentImages && establishment.establishmentImages.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {establishment.establishmentImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image.fileUrl}
                        alt={`Establishment Image ${index + 1}`}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-lg flex items-center justify-center">
                        <Button
                          variant="secondary"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => window.open(image.fileUrl, "_blank")}
                        >
                          View Full Size
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No images uploaded yet
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

